/**
 * Paste into a mission chat composer (mission stream contract phase 4,
 * agion/Agion#2327), shared by chat-ui and admin-ui:
 *
 * - spreadsheet cells, as an HTML table or as tab-separated text, become a
 *   Markdown table, so a pasted table never reaches the page as HTML;
 * - files become staged attachments through the app's own upload path and
 *   its limits;
 * - other text is left to the browser;
 * - no message text goes over MESSAGE_TEXT_MAX_BYTES.
 *
 * readPaste() decides; the app applies the decision to its composer.
 */

import { MaxUserMessageTextBytes } from './generated/missionstream'
import { unknownKind } from './messagePlacement'

/**
 * The longest message text the apps send, in UTF-8 bytes: 64 KiB, the Go
 * contract's MaxUserMessageTextBytes (generated), which the relay and the
 * host also enforce for a message sent while the agent works.
 *
 * The relay reads at most 512 KiB per browser frame (streaming-service
 * internal/relay/client.go maxMessageSize) and drops the connection on a
 * bigger one. An answer's text travels as a JSON string, where escaping can
 * turn one byte into six (a control character becomes \u00XX), so 64 KiB of
 * text is at most 384 KiB on the wire and leaves room for the frame's other
 * fields and its attachments. The composer refuses longer text with a reason.
 */
export const MESSAGE_TEXT_MAX_BYTES = MaxUserMessageTextBytes

/** The length of `text` in UTF-8 bytes. */
export function utf8ByteLength(text: string): number {
  let bytes = 0
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code < 0x80) {
      bytes += 1
    } else if (code < 0x800) {
      bytes += 2
    } else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1)
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4
        i++
      } else {
        bytes += 3
      }
    } else {
      bytes += 3
    }
  }
  return bytes
}

/** Whether `text` can be sent as one message. */
export function fitsInMessage(text: string): boolean {
  return utf8ByteLength(text) <= MESSAGE_TEXT_MAX_BYTES
}

/** A table cell as Markdown: one line, with its pipes and backslashes escaped. */
function markdownCell(cell: string): string {
  return cell
    .replace(/\s*\r?\n\s*/g, ' ')
    .trim()
    .replace(/\\/g, '\\\\')
    .replace(/\|/g, '\\|')
}

/** Rows of cells as a Markdown table; the first row is its header. */
export function markdownTable(rows: readonly (readonly string[])[]): string {
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0)
  const line = (row: readonly string[]): string => {
    const cells: string[] = []
    for (let i = 0; i < width; i++) cells.push(markdownCell(row[i] ?? ''))
    return `| ${cells.join(' | ')} |`
  }
  const separator = `| ${new Array<string>(width).fill('---').join(' | ')} |`
  const [header, ...body] = rows
  return [line(header ?? []), separator, ...body.map(line)].join('\n')
}

/** Leaves out the empty rows at the end, which spreadsheets often copy. */
function trimTrailingEmptyRows(rows: string[][]): string[][] {
  let end = rows.length
  while (end > 0 && rows[end - 1]!.every((cell) => cell.trim() === '')) end--
  return rows.slice(0, end)
}

/**
 * The rows of tab-separated text, or null when it is not a table: a table has
 * at least two rows of the same number of cells, at least two each, and is
 * not text indented with tabs.
 */
export function tsvRows(text: string): string[][] | null {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop()
  if (lines.length < 2) return null
  const rows = lines.map((line) => line.split('\t'))
  const width = rows[0]!.length
  if (width < 2 || rows.some((row) => row.length !== width)) return null
  if (rows.every((row) => row[0] === '')) return null
  return rows
}

/** A cell's text, with each line break as a space. */
function cellText(cell: Element): string {
  const copy = cell.cloneNode(true) as Element
  const breaks = copy.getElementsByTagName('br')
  while (breaks.length > 0) {
    const br = breaks[0]!
    br.parentNode!.replaceChild(copy.ownerDocument.createTextNode(' '), br)
  }
  return (copy.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/**
 * The rows of the one table a parsed clipboard page holds, or null when it
 * holds no table, several, or text outside the table, which is a copied page
 * rather than copied cells. A cell that spans columns keeps its columns, the
 * spanned ones empty.
 */
export function htmlTableRows(doc: Document): string[][] | null {
  const tables = doc.getElementsByTagName('table')
  if (tables.length !== 1) return null
  const table = tables[0] as HTMLTableElement
  const body = doc.body
  if (!body) return null
  const rest = body.cloneNode(true) as HTMLElement
  const drop = rest.querySelectorAll('table, style, script, template')
  for (let i = 0; i < drop.length; i++) drop[i]!.parentNode?.removeChild(drop[i]!)
  if ((rest.textContent ?? '').trim() !== '') return null

  const rows: string[][] = []
  for (let r = 0; r < table.rows.length; r++) {
    const row = table.rows[r]!
    const cells: string[] = []
    for (let c = 0; c < row.cells.length; c++) {
      const cell = row.cells[c]!
      cells.push(cellText(cell))
      for (let span = 1; span < cell.colSpan; span++) cells.push('')
    }
    rows.push(cells)
  }
  const kept = trimTrailingEmptyRows(rows)
  return kept.length > 0 ? kept : null
}

/** What a paste holds, as far as the composer is concerned. */
export interface ClipboardContent<F> {
  /** The text/html rendition, or ''. */
  html: string
  /** The text/plain rendition, or ''. */
  text: string
  files: readonly F[]
}

/** The text renditions and the files of a paste event's clipboard. */
export function clipboardContent(data: DataTransfer): ClipboardContent<File> {
  let files: File[] = Array.prototype.slice.call(data.files ?? [])
  if (files.length === 0 && data.items) {
    files = []
    for (let i = 0; i < data.items.length; i++) {
      const item = data.items[i]!
      const file = item.kind === 'file' ? item.getAsFile() : null
      if (file) files.push(file)
    }
  }
  return { html: data.getData('text/html') || '', text: data.getData('text/plain') || '', files }
}

/**
 * What the composer does with a paste:
 * - table: put this Markdown table in place of the selection;
 * - files: stage these files as attachments;
 * - text: let the browser paste the plain text.
 */
export type PasteAction<F> =
  | { kind: 'table'; markdown: string }
  | { kind: 'files'; files: F[] }
  | { kind: 'text' }

/**
 * What to do with a paste. Cells come first, because a spreadsheet also puts
 * a picture of them on the clipboard; then files; then plain text.
 * `parseHtml` parses the text/html rendition (DOMParser in a browser).
 */
export function readPaste<F>(clip: ClipboardContent<F>, parseHtml: (html: string) => Document): PasteAction<F> {
  const rows = /<table[\s>]/i.test(clip.html) ? htmlTableRows(parseHtml(clip.html)) : tsvRows(clip.text)
  if (rows) return { kind: 'table', markdown: markdownTable(rows) }
  if (clip.files.length > 0) return { kind: 'files', files: clip.files.slice() }
  return { kind: 'text' }
}

/**
 * The composer text with `block` in place of the selection from `start` to
 * `end`, on lines of its own so Markdown reads it as a block.
 */
export function insertBlock(value: string, start: number, end: number, block: string): string {
  const before = value.slice(0, start)
  const after = value.slice(end)
  const lead = before === '' || before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n'
  const trail = after === '' || after.startsWith('\n\n') ? '' : after.startsWith('\n') ? '\n' : '\n\n'
  return before + lead + block + trail + after
}

/** Why a paste was not taken. */
export type PasteRefusal = 'too_long' | 'no_attachments'

/** What a paste into a composer holding `value` does, as the app applies it. */
export type PasteOutcome<F> =
  | { kind: 'insert'; value: string }
  | { kind: 'attach'; files: F[] }
  | { kind: 'browser' }
  | { kind: 'refuse'; reason: PasteRefusal }

/**
 * The outcome of a paste into a composer holding `value` with the selection
 * from `start` to `end`: a table goes in as Markdown, files are staged when
 * the composer takes attachments, and nothing makes the text longer than
 * MESSAGE_TEXT_MAX_BYTES.
 */
export function pasteOutcome<F>(
  action: PasteAction<F>,
  composer: { value: string; start: number; end: number; plainText: string; canAttach: boolean },
): PasteOutcome<F> {
  const { value, start, end } = composer
  switch (action.kind) {
    case 'table': {
      const next = insertBlock(value, start, end, action.markdown)
      return fitsInMessage(next) ? { kind: 'insert', value: next } : { kind: 'refuse', reason: 'too_long' }
    }
    case 'files':
      return composer.canAttach ? { kind: 'attach', files: action.files } : { kind: 'refuse', reason: 'no_attachments' }
    case 'text':
      return fitsInMessage(value.slice(0, start) + composer.plainText + value.slice(end))
        ? { kind: 'browser' }
        : { kind: 'refuse', reason: 'too_long' }
    default:
      return unknownKind(action, { kind: 'browser' })
  }
}
